import RootState from '@/store/root_state';
import { ActionTree } from 'vuex';
import { CalendarState } from './state';
import { UserCalendarService } from '@/services/undergrad/pegasus-epona/user-calendar.service';
import { UndergradCourses } from '@/models/undergrad/undergrad-courses.model';
import oAuthPopup from '@/helpers/oAuthPopup';
import { ErrorHandler } from '@/helpers/errorHandler';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import i18n from '@/i18n';
import { UndergradDiscipline } from '@/models/undergrad/undergrad-discipline.model';
import { ActiveStudyWindow } from '@/services/undergrad/pegasus-pepe-legal/user-grades.service';

const actions: ActionTree<CalendarState, RootState> = {
  async PULL_USER_CALENDAR({ commit }): Promise<void> {
    try {
      const userCalendar = await UserCalendarService.getUserCalendar();
      commit('SET_USER_CALENDAR', userCalendar);
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('onboarding.couldntLoadCalendar').toString(),
      });
    }
  },
  START_CALENDAR_AUTH_POPUP({ dispatch, commit }): void {
    const { origin } = window.location;
    const provider = 'google';
    const redirectUri = `${origin}/graduacao/oauth2callback/google`;

    const authUrl = UserCalendarService.generateAuthUrl(provider, redirectUri);

    commit('SET_IS_CONNECTING_CALENDAR', true);

    oAuthPopup.onCallback((data) => {
      const { code, success } = data;

      if (success) {
        dispatch('INIT_USER_CALENDAR', {
          provider,
          redirectUri,
          authorizationCode: code,
        });
      }
    });

    oAuthPopup.onPopupClosed(() => {
      commit('SET_IS_CONNECTING_CALENDAR', false);
    });

    oAuthPopup.init(authUrl, provider);
  },
  async INIT_USER_CALENDAR(
    { commit, rootGetters },
    {
      provider,
      redirectUri,
      authorizationCode,
    }: { provider: string; redirectUri: string; authorizationCode: string },
  ): Promise<boolean> {
    const userUgCourses: UndergradCourses[] = rootGetters.undergradCourses ?? [];
    const allDisciplines: UndergradDiscipline[] =
      Object.values(rootGetters.undergradDisciplines) ?? [];

    const activeStudyWindows: ActiveStudyWindow[] = rootGetters.activeStudyWindows ?? [];
    const activeStudyWindowIds = activeStudyWindows.map((studyWindow) => studyWindow.id);

    const coursesIds: string[] = userUgCourses
      .map((course) => course.id)
      .filter((courseId) => !!courseId);

    const disciplineInstancesIds: string[] = allDisciplines
      .filter((discipline) => {
        const isOnActiveStudyWindow =
          discipline.studyWindowId && activeStudyWindowIds.includes(discipline.studyWindowId);

        return !!discipline.disciplineInstanceId && isOnActiveStudyWindow;
      })
      .map((discipline) => discipline.disciplineInstanceId as string);

    try {
      const userCalendar = await UserCalendarService.initUserCalendar(
        provider,
        redirectUri,
        authorizationCode,
        coursesIds,
        disciplineInstancesIds,
      );

      commit('SET_USER_CALENDAR', userCalendar);

      const { currentUser, currentUndergradCourse } = rootGetters;

      Analytics.trackEvent(EVENT_LABELS.itemClicked, {
        type: 'Sync com Agenda',
        email: currentUser?.email,
        course: currentUndergradCourse?.name,
        context: 'Onboarding',
        status: 'done',
      });
      return true;
    } catch (error) {
      ErrorHandler.showErrorMessage({
        error,
        customMessage: i18n.t('onboarding.couldntInitCalendar').toString(),
      });
    } finally {
      commit('SET_IS_CONNECTING_CALENDAR', false);
    }

    return false;
  },
};

export default actions;
